import React, { useState, useEffect, useCallback } from 'react';
import { X } from 'lucide-react';

const API_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const PopupNotification = ({ language = 'ar' }) => {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [progress, setProgress] = useState(100);
  const [hasBeenShown, setHasBeenShown] = useState(false);

  useEffect(() => {
    // Check if popups have been shown in this session
    const shownInSession = sessionStorage.getItem('popupNotificationsShown');
    if (shownInSession) {
      setHasBeenShown(true);
      return;
    }

    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await fetch(`${API_URL}/api/popup-notifications/public`);
      if (res.ok) {
        const data = await res.json();
        if (data.length > 0) {
          setNotifications(data);
          setIsVisible(true);
        }
      }
    } catch (error) {
      console.error('Error fetching popup notifications:', error);
    }
  };

  const currentNotification = notifications[currentIndex];

  const goToNext = useCallback(() => {
    if (currentIndex < notifications.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setProgress(100);
    } else {
      // All notifications shown, close and mark as shown
      setIsVisible(false);
      sessionStorage.setItem('popupNotificationsShown', 'true');
      setHasBeenShown(true);
    }
  }, [currentIndex, notifications.length]);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem('popupNotificationsShown', 'true');
    setHasBeenShown(true);
  };

  // Progress bar and auto-advance timer
  useEffect(() => {
    if (!isVisible || !currentNotification || hasBeenShown) return;

    const duration = currentNotification.duration_seconds * 1000;
    const intervalTime = 50; // Update every 50ms for smooth progress
    const decrement = (100 / duration) * intervalTime;

    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev - decrement;
        if (newProgress <= 0) {
          clearInterval(progressInterval);
          goToNext();
          return 0;
        }
        return newProgress;
      });
    }, intervalTime);

    return () => clearInterval(progressInterval);
  }, [isVisible, currentNotification, currentIndex, goToNext, hasBeenShown]);

  if (!isVisible || !currentNotification || hasBeenShown) {
    return null;
  }

  return (
    <div 
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4"
      data-testid="popup-notification-overlay"
    >
      <div 
        className="bg-gradient-to-br from-zinc-900 to-zinc-800 border border-primary/30 rounded-2xl w-full max-w-md overflow-hidden shadow-2xl shadow-primary/20 animate-in fade-in zoom-in duration-300"
        data-testid="popup-notification-modal"
      >
        {/* Progress Bar */}
        <div className="h-1 bg-zinc-700">
          <div 
            className="h-full bg-gradient-to-r from-primary to-yellow-500 transition-all duration-50 ease-linear"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 w-8 h-8 bg-zinc-800/80 hover:bg-zinc-700 rounded-full flex items-center justify-center text-white/70 hover:text-white transition-colors z-10"
          data-testid="popup-close-btn"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Image */}
        {currentNotification.image_url && (
          <div className="w-full h-40 overflow-hidden">
            <img 
              src={currentNotification.image_url}
              alt={language === 'ar' ? currentNotification.title_ar : currentNotification.title_en}
              className="w-full h-full object-cover"
              onError={(e) => e.target.parentElement.style.display = 'none'}
            />
          </div>
        )}

        {/* Content */}
        <div className="p-6 relative">
          <h3 className="text-xl font-bold text-white mb-2" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {language === 'ar' ? currentNotification.title_ar : currentNotification.title_en}
          </h3>
          <p className="text-muted-foreground leading-relaxed" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            {language === 'ar' ? currentNotification.description_ar : currentNotification.description_en}
          </p>

          {/* Notification Counter */}
          {notifications.length > 1 && (
            <div className="flex items-center justify-center gap-2 mt-4">
              {notifications.map((_, index) => (
                <div
                  key={index}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentIndex ? 'bg-primary' : 'bg-zinc-600'
                  }`}
                />
              ))}
            </div>
          )}

          {/* Skip/Next Button */}
          <div className="flex justify-center mt-4">
            <button
              onClick={goToNext}
              className="text-sm text-muted-foreground hover:text-white transition-colors"
            >
              {currentIndex < notifications.length - 1 
                ? (language === 'ar' ? 'التالي ←' : 'Next →')
                : (language === 'ar' ? 'إغلاق' : 'Close')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PopupNotification;
